import { t } from 'i18next';
import { ReactNode, useMemo } from 'react';
import { Column, useFilters, usePagination, useSortBy, useTable } from 'react-table';
import { defaultPageSizes as pageSizes } from '~components/ui/PageSizeSelector';
import { generateTableCellComponent } from '~components/ui/TableCell';
import { SsoTableSsoFragment } from './__generated__/SsoTable.graphql';
import {
  getRegistrationStatusNode,
  getSsoServiceLabel,
  SsoActionsCell,
  SsoRegistrationCell,
} from './Cells';
import { Columns } from './types';

const TableCell = generateTableCellComponent<SsoTableSsoFragment, ReactNode>();

const TableCellBold = generateTableCellComponent<SsoTableSsoFragment, ReactNode>({
  isBold: true,
});

export function useSsoTable(data: SsoTableSsoFragment[]) {
  const columns = useMemo<Array<Column<SsoTableSsoFragment>>>(
    () => [
      {
        id: Columns.Domain,
        Header: t('domain'),
        accessor: (sso) => sso.domain,
        Cell: TableCellBold,
        sortType: (rowA, rowB) => {
          const nameA = (rowA.original.domain || '').toLowerCase();
          const nameB = (rowB.original.domain || '').toLowerCase();
          return nameA.localeCompare(nameB);
        },
        width: '180px',
      },

      {
        id: Columns.SsoName,
        Header: t('ssoName'),
        accessor: (sso) => sso.provider?.displayName || '',
        Cell: TableCell,
        disableSortBy: true,
        width: '180px',
      },

      {
        id: Columns.SsoService,
        Header: t('ssoService'),
        accessor: (sso) => getSsoServiceLabel(sso),
        Cell: TableCell,
        disableSortBy: true,
        width: '120px',
      },

      {
        id: Columns.SsoStatus,
        Header: t('ssoStatus'),
        accessor: (sso) => getRegistrationStatusNode(sso),
        Cell: TableCell,
        disableSortBy: true,
        width: '180px',
      },

      {
        id: Columns.SsoRegistration,
        Header: t('ssoRegistration'),
        Cell: SsoRegistrationCell,
        disableSortBy: true,
        width: '160px',
      },

      {
        id: Columns.Actions,
        Cell: SsoActionsCell,
        width: '100px',
      },
    ],
    [],
  );

  return useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: pageSizes[0],
        sortBy: [{ id: Columns.Domain }],
        filters: [{ id: Columns.Domain, value: [] }],
      },
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetSelectedRows: false,
      autoResetPage: false,
    },
    useFilters,
    useSortBy,
    usePagination,
  );
}
